import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { FileText, Users, Calendar, Download, Search, Trash2, TrendingUp, Building2, ClipboardList } from "lucide-react";
import { Link } from "react-router-dom";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import Navbar from "@/components/Navbar";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [daily, setDaily] = useState([]);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const user = JSON.parse(localStorage.getItem("simpa_user") || "{}");

  const load = useCallback(async () => {
    try {
      const [s, l, d] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/submissions", { params: q ? { q } : {} }),
        api.get("/admin/stats/daily", { params: { days: 30 } }),
      ]);
      setStats(s.data);
      setItems(l.data.items);
      setDaily((d.data.series || []).map((p) => ({
        ...p,
        label: new Date(p.date).toLocaleDateString("id-ID", { day: "numeric", month: "short" }),
      })));
    } catch (e) {
      toast.error("Gagal memuat data");
    }
  }, [q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [q, load]);

  const del = async (id) => {
    try {
      await api.delete(`/admin/submissions/${id}`);
      toast.success("Data dihapus");
      load();
    } catch { toast.error("Gagal menghapus"); }
  };

  const cards = [
    { key: "total", label: "Total Pengajuan", value: stats?.total ?? "—", icon: FileText, tone: "blue" },
    { key: "today", label: "Hari Ini", value: stats?.today ?? "—", icon: Calendar, tone: "emerald" },
    { key: "recent", label: "7 Hari Terakhir", value: stats?.recent_7d ?? "—", icon: TrendingUp, tone: "violet" },
    { key: "forms", label: "Formulir Aktif", value: stats?.total_forms ?? "—", icon: Users, tone: "amber" },
  ];
  const toneMap = {
    blue: "bg-blue-50 text-blue-600",
    emerald: "bg-emerald-50 text-emerald-600",
    violet: "bg-violet-50 text-violet-600",
    amber: "bg-amber-50 text-amber-600",
  };

  return (
    <div className="min-h-screen bg-slate-50/60">
      <Navbar variant="admin" />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-blue-600 font-semibold">Dashboard Administrator</div>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-900">Selamat datang, {user.name || "Admin"}.</h1>
          <p className="mt-1 text-slate-600">Pantau seluruh pengajuan formulir SIMPA di sini.</p>
          <Button asChild variant="outline" className="mt-4 rounded-lg" data-testid="manage-kanim-btn">
            <Link to="/admin/kanim"><Building2 className="w-4 h-4 mr-2" /> Kelola Kantor Imigrasi</Link>
          </Button>
          <Button asChild variant="outline" className="mt-4 ml-2 rounded-lg" data-testid="manage-forms-btn">
            <Link to="/admin/formulir"><ClipboardList className="w-4 h-4 mr-2" /> Kelola Formulir</Link>
          </Button>
        </motion.div>

        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((c) => (
            <div key={c.key} className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={`stat-${c.key}`}>
              <div className={`w-10 h-10 rounded-xl grid place-items-center ${toneMap[c.tone]}`}>
                <c.icon className="w-5 h-5" />
              </div>
              <div className="mt-4 text-3xl font-semibold text-slate-900 font-[Outfit]">{c.value}</div>
              <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider">{c.label}</div>
            </div>
          ))}
        </div>

        <div className="mt-6 grid lg:grid-cols-3 gap-6">
          {/* Trend chart */}
          <div className="rounded-2xl bg-white border border-slate-200 p-6 lg:col-span-3" data-testid="trend-chart">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <div>
                <h3 className="font-semibold text-slate-900 tracking-tight">Tren Pengajuan 30 Hari Terakhir</h3>
                <p className="text-xs text-slate-500">Jumlah formulir yang dibuat pemohon per hari.</p>
              </div>
              <Badge variant="outline" className="text-xs text-slate-600">
                Total: {daily.reduce((s, d) => s + d.count, 0)}
              </Badge>
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={daily} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradBlue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#1a3a6b" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#1a3a6b" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} interval={Math.ceil(daily.length / 10)} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: "10px", border: "1px solid #e2e8f0", fontSize: 12 }}
                    labelFormatter={(label, p) => (p?.[0] ? new Date(p[0].payload.date).toLocaleDateString("id-ID", { dateStyle: "full" }) : label)}
                    formatter={(v) => [`${v} pengajuan`, ""]}
                  />
                  <Area type="monotone" dataKey="count" stroke="#1a3a6b" strokeWidth={2} fill="url(#gradBlue)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-2xl bg-white border border-slate-200 p-6 lg:col-span-1">
            <h3 className="font-semibold text-slate-900 tracking-tight">Distribusi Formulir</h3>
            <div className="mt-5 space-y-4">
              {(stats?.by_form || []).map((f) => {
                const pct = stats.total ? Math.round((f.count / stats.total) * 100) : 0;
                return (
                  <div key={f.form_id}>
                    <div className="flex justify-between text-sm mb-1.5">
                      <span className="text-slate-700 truncate pr-2">{f.form_title}</span>
                      <span className="text-slate-500 font-medium">{f.count}</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
              {(!stats?.by_form || stats.by_form.length === 0) && (
                <p className="text-sm text-slate-500">Belum ada data pengajuan.</p>
              )}
            </div>
          </div>

          <div className="rounded-2xl bg-white border border-slate-200 p-6 lg:col-span-2">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-semibold text-slate-900 tracking-tight">Pengajuan Terbaru</h3>
              <div className="relative w-full sm:w-72">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input placeholder="Cari nama atau NIK..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-10 rounded-lg" data-testid="admin-search-input" />
              </div>
            </div>
            <div className="mt-5 overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Pemohon</TableHead>
                    <TableHead>Formulir</TableHead>
                    <TableHead>Tanggal</TableHead>
                    <TableHead className="text-right">Aksi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-slate-500 py-10">Belum ada pengajuan.</TableCell>
                    </TableRow>
                  )}
                  {items.map((r) => (
                    <TableRow key={r.id} data-testid={`submission-row-${r.id}`}>
                      <TableCell>
                        <div className="font-medium text-slate-900">{r.applicant_name}</div>
                        <div className="text-xs text-slate-500">NIK: {r.biodata?.nik || "—"}</div>
                      </TableCell>
                      <TableCell><Badge variant="outline">{r.form_title}</Badge></TableCell>
                      <TableCell className="text-slate-600 text-sm">
                        {new Date(r.created_at).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button asChild variant="ghost" size="sm" data-testid={`download-${r.id}`}>
                          <a href={`${API}/submissions/${r.id}/pdf`} target="_blank" rel="noreferrer">
                            <Download className="w-4 h-4" />
                          </a>
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600" data-testid={`delete-${r.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Hapus pengajuan?</AlertDialogTitle>
                              <AlertDialogDescription>Data pengajuan atas nama <b>{r.applicant_name}</b> akan dihapus permanen.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Batal</AlertDialogCancel>
                              <AlertDialogAction onClick={() => del(r.id)} className="bg-red-500 hover:bg-red-600">Hapus</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
